import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";

type SwitchableCollectionContextType = {
  switchedEntities: number[];
  toggleEntityState: (projectId: number) => void;
  setSwitchedEntity: (projectId: number, toggle: boolean) => void;
};

const SwitchableCollectionContext = createContext<
  SwitchableCollectionContextType | undefined
>(undefined);

type OpenedProjectsProviderProps = {
  persistenceKey?: string;
};

export const SwitchableCollectionProvider = (
  props: PropsWithChildren<OpenedProjectsProviderProps>,
) => {
  const { persistenceKey } = props;
  const [switchedEntities, setSwitchedEntity] = useState<Set<number>>(() => {
    if (!persistenceKey) {
      return new Set();
    }

    let startingState: Set<number>;

    const persisted = localStorage.getItem(persistenceKey);
    if (persisted) {
      try {
        const maybeValidState = JSON.parse(persisted);
        const isValid =
          Array.isArray(maybeValidState) &&
          maybeValidState.every((id: unknown) => typeof id === "number");

        startingState = isValid ? new Set(maybeValidState) : new Set();
      } catch (e) {
        startingState = new Set();
      }
    } else {
      startingState = new Set();
    }

    return startingState;
  });
  useEffect(() => {
    if (persistenceKey !== undefined) {
      localStorage.setItem(
        persistenceKey,
        JSON.stringify([...switchedEntities]),
      );
    }
  }, [switchedEntities, persistenceKey]);

  return (
    <SwitchableCollectionContext.Provider
      value={{
        switchedEntities: [...switchedEntities],
        setSwitchedEntity: (projectId, toggle) => {
          setSwitchedEntity((prev) => {
            if (toggle) {
              prev.add(projectId);
            } else {
              prev.delete(projectId);
            }

            return new Set(prev);
          });
        },
        toggleEntityState: (projectId) => {
          setSwitchedEntity((prev) => {
            if (prev.has(projectId)) {
              prev.delete(projectId);
            } else {
              prev.add(projectId);
            }

            return new Set(prev);
          });
        },
      }}
    >
      {props.children}
    </SwitchableCollectionContext.Provider>
  );
};

type SwitchableCollectionApiProps = {
  children: (ctx: SwitchableCollectionContextType) => React.ReactNode;
};

export const SwitchableCollectionApi = (props: SwitchableCollectionApiProps) =>
  props.children(useSwitchableCollection());

export const useSwitchableCollection = () => {
  const context = useContext(SwitchableCollectionContext);

  if (!context) {
    throw new Error(
      "useSwitchableCollection must be used within a SwitchableCollectionProvider",
    );
  }

  return context;
};
